// utils/parseNumericFilter.js

/**
 * Парсить числовий фільтр з підтримкою логічних умов.
 *
 * Підтримувані формати:
 *   просте число:   25        → дорівнює 25
 *   порівняння:     >10       → більше 10
 *                   >=10      → більше або дорівнює 10
 *                   <50       → менше 50
 *                   <=50      → менше або дорівнює 50
 *   діапазон:       5-20      → від 5 до 20 включно
 *   логічне AND:    >2 && <5  → більше 2 І менше 5
 *                   >=10 && <=20 && !=15
 *   логічне OR:     <3 || >10 → менше 3 АБО більше 10
 *   нерівність:     !=5       → не дорівнює 5
 *
 * Пріоритет: && вищий за ||  (як у більшості мов)
 * Змішування && і || підтримується: >1 && <10 || >20 && <30
 *
 * Валідація:
 *   - незрозумілий токен (напр. "abc", "5-", "><10") → помічається як помилка
 *   - логічно неможлива AND-група (напр. "8-30 && >200" — число не може
 *     одночасно лежати в [8,30] і бути більшим за 200) → теж помилка,
 *     обчислюється через перетин інтервалів усіх токенів групи
 */

// Розбирає один токен. Повертає:
//   { test, valid: true, interval }               — розпізнано
//   { test: () => true, valid: false, error }      — не розпізнано
const parseToken = (rawToken) => {
  const t = rawToken.trim();

  const invalid = (customMsg) => ({
    test: () => true,
    valid: false,
    error: customMsg || `незрозумілий вираз «${t}»`,
    interval: null,
  });

  if (!t) return invalid('пропущено умову (порожній вираз між && або ||)');

  const numRe = /^-?\d+(?:\.\d+)?$/;

  if (t.startsWith('>=')) {
    const s = t.slice(2).trim();
    if (!numRe.test(s)) return invalid();
    const n = parseFloat(s);
    return { test: v => v >= n, valid: true, interval: { lo: n, loInc: true, hi: Infinity, hiInc: true } };
  }
  if (t.startsWith('<=')) {
    const s = t.slice(2).trim();
    if (!numRe.test(s)) return invalid();
    const n = parseFloat(s);
    return { test: v => v <= n, valid: true, interval: { lo: -Infinity, loInc: true, hi: n, hiInc: true } };
  }
  if (t.startsWith('!=')) {
    const s = t.slice(2).trim();
    if (!numRe.test(s)) return invalid();
    const n = parseFloat(s);
    // "!=" — це виняток, а не інтервал; на перетин інтервалів не впливає
    return { test: v => v !== n, valid: true, interval: null };
  }
  if (t.startsWith('>')) {
    const s = t.slice(1).trim();
    if (!numRe.test(s)) return invalid();
    const n = parseFloat(s);
    return { test: v => v > n, valid: true, interval: { lo: n, loInc: false, hi: Infinity, hiInc: true } };
  }
  if (t.startsWith('<')) {
    const s = t.slice(1).trim();
    if (!numRe.test(s)) return invalid();
    const n = parseFloat(s);
    return { test: v => v < n, valid: true, interval: { lo: -Infinity, loInc: true, hi: n, hiInc: false } };
  }

  // діапазон: 5-20  (не плутати з від'ємними числами типу -5)
  const rangeMatch = t.match(/^(-?\d+(?:\.\d+)?)-(-?\d+(?:\.\d+)?)$/);
  if (rangeMatch) {
    const lo = parseFloat(rangeMatch[1]);
    const hi = parseFloat(rangeMatch[2]);
    if (lo > hi) return { test: () => true, valid: false, error: `у діапазоні «${t}» початок більший за кінець`, interval: null };
    return { test: v => v >= lo && v <= hi, valid: true, interval: { lo, loInc: true, hi, hiInc: true } };
  }

  // точне число
  if (numRe.test(t)) {
    const n = parseFloat(t);
    return { test: v => v === n, valid: true, interval: { lo: n, loInc: true, hi: n, hiInc: true } };
  }

  return invalid();
};

// Перетинає інтервали AND-групи. Повертає null якщо перетин порожній
// (умова логічно неможлива), інакше сам перетин (для інформації).
const intersectIntervals = (intervals) => {
  let lo = -Infinity, loInc = true, hi = Infinity, hiInc = true;
  intervals.forEach(iv => {
    if (!iv) return; // != або порожній токен — не звужує інтервал
    if (iv.lo > lo || (iv.lo === lo && !iv.loInc)) { lo = iv.lo; loInc = iv.loInc; }
    if (iv.hi < hi || (iv.hi === hi && !iv.hiInc)) { hi = iv.hi; hiInc = iv.hiInc; }
  });
  if (lo > hi) return null;
  if (lo === hi && !(loInc && hiInc)) return null;
  return { lo, loInc, hi, hiInc };
};

// Розбирає всю групу (розділену &&) з валідацією логічної несуперечності
const parseAndGroup = (group) => {
  const tokens = group.split('&&').map(t => parseToken(t));
  const badToken = tokens.find(t => !t.valid);
  if (badToken) return { test: () => true, valid: false, error: badToken.error };

  const intervals = tokens.map(t => t.interval);
  const merged = intersectIntervals(intervals);
  if (merged === null && intervals.some(iv => iv !== null)) {
    return {
      test: () => true,
      valid: false,
      error: `умови в групі «${group.trim()}» взаємовиключні — жодне число не підходить одночасно`,
    };
  }

  return { test: v => tokens.every(t => t.test(v)), valid: true };
};

/**
 * Перевіряє коректність рядка фільтра (синтаксично і логічно).
 * Повертає { valid: true } або { valid: false, error: string }.
 */
export const validateFilter = (filterStr) => {
  const str = (filterStr || '').trim();
  if (!str) return { valid: true };

  for (const group of str.split('||')) {
    const parsed = parseAndGroup(group);
    if (!parsed.valid) return { valid: false, error: parsed.error };
  }
  return { valid: true };
};

/**
 * Компілює рядок фільтра у функцію (value: number) => boolean.
 * Повертає null якщо рядок порожній.
 * Якщо рядок некоректний (див. validateFilter) — фільтр не застосовується
 * (пропускає всі значення), щоб не ховати дані від користувача мовчки.
 */
export const compileNumericFilter = (filterStr) => {
  const str = (filterStr || '').trim();
  if (!str) return null;

  const orGroups = str.split('||').map(group => parseAndGroup(group));
  if (orGroups.some(g => !g.valid)) return () => true;

  return v => orGroups.some(g => g.test(v));
};

/**
 * Перевіряє чи відповідає числове значення рядку фільтра.
 * Зручна обгортка для використання напряму в .filter().
 */
export const matchesNumericFilter = (value, filterStr) => {
  const fn = compileNumericFilter(filterStr);
  if (!fn) return true;
  return fn(Number(value));
};

/**
 * Повертає людиночитаний опис фільтра (для тултіпу / підказки),
 * або текст помилки, якщо фільтр некоректний.
 */
export const describeFilter = (filterStr) => {
  const str = (filterStr || '').trim();
  if (!str) return '';

  const check = validateFilter(str);
  if (!check.valid) return `⚠ ${check.error}`;

  const describeToken = (t) => {
    t = t.trim();
    if (t.startsWith('>=')) return `≥ ${t.slice(2)}`;
    if (t.startsWith('<=')) return `≤ ${t.slice(2)}`;
    if (t.startsWith('!=')) return `≠ ${t.slice(2)}`;
    if (t.startsWith('>'))  return `> ${t.slice(1)}`;
    if (t.startsWith('<'))  return `< ${t.slice(1)}`;
    const rangeMatch = t.match(/^(-?\d+(?:\.\d+)?)-(-?\d+(?:\.\d+)?)$/);
    if (rangeMatch) return `від ${rangeMatch[1]} до ${rangeMatch[2]}`;
    return `= ${t}`;
  };

  return str
    .split('||')
    .map(group =>
      group.split('&&').map(t => describeToken(t)).join(' і ')
    )
    .join(' або ');
};